/**
 * What the customer has to do next to sign, in the order they do it.
 *
 * The signing screen has always known when it was not ready — the Sign button
 * greys out and one line of small print underneath says why. That is enough for
 * somebody who already knows the shape of the form; it is not enough for a
 * homeowner on a phone at their kitchen table, who sees a dead button and no
 * sense of how far off they are.
 *
 * Jonathon, 2026-09-07: "when the customer is going through the agreement to
 * esign it needs to highlight what they need to do next, like click on read the
 * agreement, check the box, put your name in, sign this agreement."
 *
 * So the page draws the list, ticks off what is done, and points at the one
 * thing to do now. The list lives here rather than in the component because it
 * has to agree with the Sign button, and the Sign button's rule
 * (`signatureIsComplete`) is already in `packages/core` — two copies of "what
 * makes a signature complete" would drift, and the version the customer reads
 * is the one that would go wrong quietly.
 *
 * The steps are only ever a description of the same conditions. Nothing here
 * decides whether a signature may be written: the button reads
 * `signatureIsComplete` as it always has, and the Edge Function checks again
 * server-side because this is JavaScript on somebody else's phone.
 */

/** The things a customer does, in page order. */
export type SigningStepId =
  "choose" | "read" | "initial" | "consent" | "name" | "sign";

export interface SigningStep {
  id: SigningStepId;
  /** What to do, in the customer's words. Shown as written. */
  label: string;
  /** Behind them. Drawn ticked. */
  done: boolean;
}

export interface SigningStepsInput {
  /** Whether this document offered a choice of package at all. */
  needsAChoice: boolean;
  /**
   * What they picked: one id, a list of them where the document lets them buy
   * more than one (ADR 0120), or nothing yet. An empty list is nothing.
   */
  chosen: string | readonly string[] | null;
  /** Whether the document lets them buy more than one of its packages. */
  chooseMany?: boolean;
  /** Whether the agreement is open on screen. */
  agreementOpen: boolean;
  /** The initial boxes this frozen document actually draws. Usually none. */
  requiredInitials: readonly string[];
  /** Which of them have been initialled. */
  initials: Readonly<Record<string, unknown>>;
  /** The electronic-signature box. */
  consented: boolean;
  /** What they have typed into the name box. */
  signerName: string;
}

/**
 * The steps this document asks for, ticked as far as they have got.
 *
 * Steps that do not apply are absent rather than pre-ticked: a single-price
 * proposal never asks anybody to choose a package, and a contract written today
 * has no initial boxes (ADR 0032 froze six of them onto documents sent before
 * 2026-08-13, which is why the caller passes what its own snapshot draws). A
 * list with a tick against something the customer was never asked to do reads
 * as a screen that has lost track of them.
 *
 * The date is deliberately not a step. It fills itself in with today and is
 * right unless somebody changes it, so it is a correction rather than a thing
 * to go and do — the page keeps its own message for that.
 */
export function signingSteps(input: SigningStepsInput): SigningStep[] {
  const steps: SigningStep[] = [];

  if (input.needsAChoice) {
    const picked =
      typeof input.chosen === "string"
        ? input.chosen !== ""
        : (input.chosen?.length ?? 0) > 0;
    steps.push({
      id: "choose",
      // A document that sells more than one thing is not asking them to pick a
      // package, and "Choose your package" there reads as "only one".
      label: input.chooseMany ? "Choose what you want" : "Choose your package",
      done: picked,
    });
  }

  steps.push({
    id: "read",
    label: "Read the agreement",
    done: input.agreementOpen,
  });

  if (input.requiredInitials.length > 0) {
    const left = input.requiredInitials.filter(
      (key) => !input.initials[key],
    ).length;
    steps.push({
      id: "initial",
      label:
        left === 0 ? "Initial each box" : `Initial each box — ${left} to go`,
      done: left === 0,
    });
  }

  steps.push({
    id: "consent",
    label: "Check the box to sign electronically",
    done: input.consented,
  });

  steps.push({
    id: "name",
    // `signatureIsComplete` wants two characters, so a single initial is not a
    // name here either. The step stays open rather than ticking on the first
    // keystroke and then blocking the button anyway.
    label: "Type your full name",
    done: input.signerName.trim().length >= 2,
  });

  // Never done: it is done by leaving the page signed.
  steps.push({ id: "sign", label: "Sign this agreement", done: false });

  return steps;
}

/**
 * The one to point at — the first thing not yet done.
 *
 * Always a step, because "sign" is never done, so the page always has something
 * to highlight rather than falling silent at the moment it matters most.
 */
export function nextSigningStep(input: SigningStepsInput): SigningStep {
  const steps = signingSteps(input);
  // The last is "sign", which is never done, so this cannot be undefined.
  return steps.find((step) => !step.done) ?? steps[steps.length - 1]!;
}
